import React, { useEffect } from 'react'
import { logger } from '../utils'

import SubscriptionManager from '../connection/subscription'
import SportManager from '../managers/sport'

import { useUpdateEffect } from '../ui'

import {
  BESPOKE_MARKET_UPDATE,
  BESPOKE_MARKETS_STATE_UPDATE
} from '../actions'
import Bus from '../bus'
import BespokeMarketManager from '../managers/bespoke-market'
import { useSettings } from './settings'

export const BespokeMarketContext = React.createContext()

export function useBespokeMarketsState () {
  return React.useContext(BespokeMarketContext) || marketsInitialState
}

export const marketsInitialState = {
  loading: true,
  initialized: false
}

/*
  Should become cleaner in future
*/
export const bespokeMarketsReducer = (marketsState, action) => {
  logger.debug('[bespoke-markets] dispatching action', action)
  switch (action.type) {
    case BESPOKE_MARKET_UPDATE: {
      const market = action.payload

      const { markets } = marketsState

      // delete market from state if it becomes inactive for any reason
      if (market.status !== 'NORMAL') {
        return {
          ...marketsState,
          markets: markets.filter(({ id }) => id !== market.id)
        }
      }

      // replace if market already exists in state
      if (markets.some(({ id }) => id === market.id)) {
        return {
          ...marketsState,
          markets: markets.reduce(
            (acc, cur) => [...acc, cur.id === market.id ? market : cur],
            []
          )
        }
      }

      // add new market
      return {
        ...marketsState,
        markets: [...markets, market]
      }
    }
    case BESPOKE_MARKETS_STATE_UPDATE:
      return {
        ...marketsState,
        ...action.payload
      }
    default:
      return marketsState
  }
}

const createBespokeMarketsInitialState = (options) => {
  return {
    markets: [],
    ...options,
    loading: false,
    initialized: true
  }
}

export const createBespokeMarketsState = (options) => {
  const [marketsState, marketsDispatcher] = React.useReducer(
    bespokeMarketsReducer,
    options,
    createBespokeMarketsInitialState
  )

  const { lang } = useSettings()

  const fetchBespokeMarkets = async (ac = null) => {
    marketsDispatcher({
      type: BESPOKE_MARKETS_STATE_UPDATE,
      payload: {
        markets: [],
        loading: true
      }
    })

    BespokeMarketManager.getAvailableMarkets(
      null,
      {
        currentPage: 0,
        itemsPerPage: 100
      },
      lang,
      ac?.signal
    )
      .then((data) => {
        marketsDispatcher({
          type: BESPOKE_MARKETS_STATE_UPDATE,
          payload: {
            markets: data?.markets,
            loading: false
          }
        })
      })
      .catch((e) => {
        marketsDispatcher({
          type: BESPOKE_MARKETS_STATE_UPDATE,
          payload: {
            error: e,
            loading: false
          }
        })
      })
  }

  /*
    Subscribу to bespoke markets updates
  */
  React.useEffect(
    () => {
      const topics = [
        {
          topic: SubscriptionManager.subscriptionTopics.BESPOKE_MARKET_UPDATE,
          key: ''
        }
      ]

      SubscriptionManager.notifierSubscribe(topics)

      const handleBespokeMarketUpdate = ({ data }) => {
        logger.info(`[bespokeMarket:update]: ${data}`)

        marketsDispatcher({
          type: BESPOKE_MARKET_UPDATE,
          payload: data
        })
      }

      const ac = new AbortController()

      fetchBespokeMarkets(ac).finally(() => {
        logger.debug('BespokeMarketService.fetchBespokeMarkets done')
      })

      Bus.on(Bus.events.bespokeMarket.update, handleBespokeMarketUpdate)
      Bus.on(Bus.events.bespokeMarket.reload, fetchBespokeMarkets)

      return () => {
        SubscriptionManager.notifierUnsubscribe(topics)
        Bus.off(Bus.events.bespokeMarket.update, handleBespokeMarketUpdate)
        Bus.off(Bus.events.bespokeMarket.reload, fetchBespokeMarkets)
        ac.abort()
      }
    },
    []
  )

  return [marketsState, marketsDispatcher]
}

export function BespokeMarketsProvider (props) {
  const { children } = props

  const [eventState] = createBespokeMarketsState()

  return (
    <BespokeMarketContext.Provider value={eventState}>
      {children}
    </BespokeMarketContext.Provider>
  )
}
